import { apiRequest } from '../../../shared/services/apiClient';
import {
  CreateEmployeeRequest,
  CurrentEmployeeResponse,
  Employee,
  EmployeeAiProfile,
  EmployeeContractType,
  EmployeeDepartment,
  EmployeeListQuery,
  EmployeeLocation,
  EmployeePosition,
  EmployeesMetadata,
  LanguageOption,
  MetadataOption,
  UpdateEmployeeAiProfileRequest,
  UpdateEmployeeRequest,
} from '../types/Employee';

/** Backend stores UTC; if the payload omits a timezone, treat it as UTC so UI local formatting is correct. */
const normalizeUtcTimestamp = (value: unknown): string | undefined => {
  if (value == null) return undefined;

  const raw = String(value).trim();
  if (!raw) return undefined;

  const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(raw);
  const utcCandidate = hasTimezone ? raw : `${raw}Z`;
  const parsed = new Date(utcCandidate);
  if (Number.isNaN(parsed.getTime())) return undefined;

  return parsed.toISOString();
};

const normalizeEmployee = (value: any): Employee => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  firstName: value?.firstName ? String(value.firstName) : undefined,
  lastName: value?.lastName ? String(value.lastName) : undefined,
  email: String(value?.email ?? ''),
  title: value?.title ? String(value.title) : undefined,
  dateOfBirth: value?.dateOfBirth ? String(value.dateOfBirth) : undefined,
  employmentDate: value?.employmentDate ? String(value.employmentDate) : undefined,
  status: value?.status ? String(value.status) : undefined,
  contractTypeId: value?.contractTypeId ? String(value.contractTypeId) : undefined,
  contractTypeName: value?.contractTypeName ? String(value.contractTypeName) : undefined,
  positionId: value?.positionId ? String(value.positionId) : undefined,
  positionName: value?.positionName ? String(value.positionName) : undefined,
  departmentId: value?.departmentId ? String(value.departmentId) : undefined,
  departmentName: value?.departmentName ? String(value.departmentName) : undefined,
  phoneCountryCode: value?.phoneCountryCode ? String(value.phoneCountryCode) : undefined,
  phoneNumber: value?.phoneNumber ? String(value.phoneNumber) : undefined,
  pbxExtension: value?.pbxExtension ? String(value.pbxExtension) : undefined,
  language: value?.language ? String(value.language) : undefined,
  calendarPreference: value?.calendarPreference ? String(value.calendarPreference) : undefined,
  locationId: value?.locationId ? String(value.locationId) : undefined,
  locationName: value?.locationName ? String(value.locationName) : undefined,
  colorPreference: value?.colorPreference ? String(value.colorPreference) : undefined,
  profileImageUrl: value?.profileImageUrl ? String(value.profileImageUrl) : undefined,
  isActive: Boolean(value?.isActive),
  requirePasswordChangeOnFirstLogin: Boolean(value?.requirePasswordChangeOnFirstLogin),
  lastLoginAt: normalizeUtcTimestamp(value?.lastLoginAt),
  createdAt: value?.createdAt ? String(value.createdAt) : undefined,
  updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
});

const normalizeMetadataOption = (value: any): MetadataOption => ({
  value: String(value?.value ?? ''),
  label: String(value?.label ?? value?.value ?? ''),
});

const normalizeLanguageOption = (value: any): LanguageOption => ({
  code: String(value?.code ?? ''),
  name: String(value?.name ?? value?.code ?? ''),
});

const DEFAULT_EMPLOYEE_LANGUAGES: LanguageOption[] = [
  { code: 'az', name: 'Azerbaijani' },
  { code: 'ru', name: 'Russian' },
  { code: 'tr', name: 'Turkish' },
];

const mergeEmployeeLanguageOptions = (options: LanguageOption[]): LanguageOption[] => {
  const merged = [...options];
  const knownCodes = new Set(
    options
      .map(option => (option.code || '').trim().toLowerCase())
      .filter(Boolean)
  );

  for (const option of DEFAULT_EMPLOYEE_LANGUAGES) {
    const code = option.code.trim().toLowerCase();
    if (knownCodes.has(code)) {
      continue;
    }

    merged.push(option);
    knownCodes.add(code);
  }

  return merged;
};

const normalizePosition = (value: any): EmployeePosition => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  departmentId: value?.departmentId ? String(value.departmentId) : undefined,
  departmentName: value?.departmentName ? String(value.departmentName) : undefined,
  createdAt: value?.createdAt ? String(value.createdAt) : undefined,
  updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
});

const normalizeContractType = (value: any): EmployeeContractType => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  createdAt: value?.createdAt ? String(value.createdAt) : undefined,
  updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
});

const normalizeDepartment = (value: any): EmployeeDepartment => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  parentDepartmentId: value?.parentDepartmentId ? String(value.parentDepartmentId) : undefined,
  isActive: Boolean(value?.isActive),
  createdAt: value?.createdAt ? String(value.createdAt) : undefined,
  updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
});

const normalizeLocation = (value: any): EmployeeLocation => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  code: value?.code ? String(value.code) : undefined,
  isPrimary: Boolean(value?.isPrimary),
  isActive: Boolean(value?.isActive),
});

const normalizeEmployeesMetadata = (value: any): EmployeesMetadata => ({
  statuses: Array.isArray(value?.statuses) ? value.statuses.map(normalizeMetadataOption) : [],
  calendarPreferences: Array.isArray(value?.calendarPreferences) ? value.calendarPreferences.map(normalizeMetadataOption) : [],
  languages: mergeEmployeeLanguageOptions(
    Array.isArray(value?.languages) ? value.languages.map(normalizeLanguageOption) : []
  ),
  positions: Array.isArray(value?.positions) ? value.positions.map(normalizePosition) : [],
  contractTypes: Array.isArray(value?.contractTypes) ? value.contractTypes.map(normalizeContractType) : [],
  departments: Array.isArray(value?.departments) ? value.departments.map(normalizeDepartment) : [],
  locations: Array.isArray(value?.locations) ? value.locations.map(normalizeLocation) : [],
});

const normalizeCurrentEmployeeResponse = (value: any): CurrentEmployeeResponse | null => {
  const employeeValue = value?.employee ?? value?.data?.employee;
  const tenantIdValue = value?.tenantId ?? value?.data?.tenantId;
  if (!employeeValue || !tenantIdValue) {
    return null;
  }

  return {
    employee: normalizeEmployee(employeeValue),
    tenantId: String(tenantIdValue),
  };
};

const normalizeEmployeeAiProfile = (value: any): EmployeeAiProfile | null => {
  const item = pickSingle(value);
  if (!item?.employeeId) {
    return null;
  }

  return {
    employeeId: String(item.employeeId),
    aboutMeForAi: item.aboutMeForAi ? String(item.aboutMeForAi) : undefined,
    responsePreferences: item.responsePreferences ? String(item.responsePreferences) : undefined,
    expertiseAreas: item.expertiseAreas ? String(item.expertiseAreas) : undefined,
    publicTeamSummary: item.publicTeamSummary ? String(item.publicTeamSummary) : undefined,
    createdAt: item.createdAt ? String(item.createdAt) : undefined,
    updatedAt: item.updatedAt ? String(item.updatedAt) : undefined,
  };
};

const pickItems = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
};

const pickSingle = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

const toQueryString = (query?: EmployeeListQuery): string => {
  if (!query) return '';

  const params = new URLSearchParams();

  if (query.q && query.q.trim()) params.set('Q', query.q.trim());
  if (typeof query.isActive === 'boolean') params.set('IsActive', String(query.isActive));
  if (query.locationId) params.set('LocationId', query.locationId);
  if (query.sortBy) params.set('SortBy', query.sortBy);
  if (query.sortDir) params.set('SortDir', query.sortDir);
  if (typeof query.page === 'number') params.set('Page', String(query.page));
  if (typeof query.pageSize === 'number') params.set('PageSize', String(query.pageSize));

  const search = params.toString();
  return search ? `?${search}` : '';
};

export const employeeService = {
  getEmployees: async (query?: EmployeeListQuery): Promise<Employee[]> => {
    const payload = await apiRequest<any>(`/api/v1/employees${toQueryString(query)}`);
    return pickItems(payload).map(normalizeEmployee);
  },

  getEmployeesMetadata: async (): Promise<EmployeesMetadata> => {
    const payload = await apiRequest<any>('/api/v1/employees/metadata');
    return normalizeEmployeesMetadata(pickSingle(payload));
  },

  getEmployeeById: async (id: string): Promise<Employee | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/employees/${id}`);
      const item = pickSingle(payload);
      return item ? normalizeEmployee(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  },

  getCurrentEmployee: async (): Promise<CurrentEmployeeResponse | null> => {
    try {
      const payload = await apiRequest<any>('/api/v1/employees/me');
      return normalizeCurrentEmployeeResponse(payload);
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  },

  getMyAiProfile: async (): Promise<EmployeeAiProfile | null> => {
    try {
      const payload = await apiRequest<any>('/api/v1/employees/me/ai-profile');
      return normalizeEmployeeAiProfile(payload);
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  },

  updateMyAiProfile: async (request: UpdateEmployeeAiProfileRequest): Promise<EmployeeAiProfile | null> => {
    const payload = await apiRequest<any>('/api/v1/employees/me/ai-profile', {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    return normalizeEmployeeAiProfile(payload);
  },

  createEmployee: async (request: CreateEmployeeRequest): Promise<Employee | null> => {
    const payload = await apiRequest<any>('/api/v1/employees', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeEmployee(item) : null;
  },

  updateEmployee: async (id: string, updates: UpdateEmployeeRequest): Promise<Employee | null> => {
    try {
      const payload = await apiRequest<any>(`/api/v1/employees/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      const item = pickSingle(payload);
      return item ? normalizeEmployee(item) : null;
    } catch (error) {
      if (error instanceof Error && /404|not found/i.test(error.message)) {
        return null;
      }
      throw error;
    }
  },

  uploadMyProfileImage: async (file: File): Promise<Employee | null> => {
    const formData = new FormData();
    formData.append('file', file);

    const payload = await apiRequest<any>('/api/v1/employees/me/profile-image', {
      method: 'POST',
      body: formData,
    });

    const item = pickSingle(payload);
    return item ? normalizeEmployee(item) : null;
  },

  removeMyProfileImage: async (): Promise<Employee | null> => {
    const payload = await apiRequest<any>('/api/v1/employees/me/profile-image', {
      method: 'DELETE',
    });

    const item = pickSingle(payload);
    return item ? normalizeEmployee(item) : null;
  },
};

export const getEmployees = employeeService.getEmployees;
export const getEmployeesMetadata = employeeService.getEmployeesMetadata;
export const getEmployeeById = employeeService.getEmployeeById;
export const getCurrentEmployee = employeeService.getCurrentEmployee;
export const getMyAiProfile = employeeService.getMyAiProfile;
export const updateMyAiProfile = employeeService.updateMyAiProfile;
export const createEmployee = employeeService.createEmployee;
export const updateEmployee = employeeService.updateEmployee;
export const uploadMyProfileImage = employeeService.uploadMyProfileImage;
export const removeMyProfileImage = employeeService.removeMyProfileImage;
